import { useTranslation } from 'react-i18next';

import { logError } from 'services/logging/logging';
import {
  useCommentReview,
  useDeleteCommentFromReview,
  useApproveReview as useMessageApprove,
} from 'services/repositories/reviews/reviews';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { GenericError } from 'support/helpers/errors/errors';
import { DocumentTypeWithVirtual, MessageV2DTO, ValueOf } from 'support/types';

type ApprovedDecision = Parameters<ReturnType<typeof useMessageApprove>['mutate']>[0]['approved_decision'];

type ReviewComment = NonNullable<MessageV2DTO['review']>['comments'][number];

type UseHandlePendingReviewProps = {
  reviewId: string | undefined;
  onCommentChange: (comment: ReviewComment | undefined) => void;
  onEditingChange: (isEditing: boolean) => void;
  currentComment?: ReviewComment;
  virtualDocumentType: ValueOf<DocumentTypeWithVirtual>;
};

export const useHandlePendingReview = ({
  reviewId,
  onCommentChange,
  onEditingChange,
  currentComment,
  virtualDocumentType,
}: UseHandlePendingReviewProps) => {
  const { t } = useTranslation();
  const { mutate: messageApprove, isLoading: isApprovingMessage, isSuccess: isMessageApproved } = useMessageApprove();
  const { mutate: commentOnReview } = useCommentReview();
  const { mutate: deleteComment } = useDeleteCommentFromReview();

  const handleApprove = (annotationIds: Array<string> = [], approvedDecision?: ApprovedDecision) => {
    if (!reviewId) {
      logError(new GenericError('PendingReviewBanner: Cannot approve review with undefined reviewId', { reviewId }));
      return;
    }

    messageApprove(
      {
        id: reviewId,
        ...(approvedDecision && { approved_decision: approvedDecision }),
        ...(annotationIds.length > 0 ? { annotation_ids: annotationIds } : {}),
      },
      {
        onSuccess: () => {
          addNotification({
            title: t('transactions:show.sections.alert.review.notification.approved.title'),
            subtitle: t('transactions:show.sections.alert.review.notification.approved.subtitle', {
              documentType: t(`common:messageTypes.singular.${virtualDocumentType}`),
            }),
          });
        },
        onError: (error) => {
          logError(error);
          addNotification(
            {
              title: t('transactions:show.sections.alert.review.notification.approved.error.title'),
              subtitle: t('transactions:show.sections.alert.review.notification.approved.error.subtitle'),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  const handleComment = (comment: string) => {
    if (!reviewId) {
      logError(
        new GenericError('PendingReviewBanner: Cannot comment on review with undefined reviewId', {
          reviewId,
          comment,
        }),
      );
      return;
    }

    onEditingChange(false);
    commentOnReview(
      { id: reviewId, comment },
      {
        onError: (error) => {
          logError(error);
          addNotification(
            {
              title: t('transactions:show.sections.alert.review.notification.comment.error.title'),
              subtitle: t('transactions:show.sections.alert.review.notification.comment.error.subtitle'),
            },
            NotificationType.error,
          );
          onEditingChange(true); // revert to editing state if comment fails
        },
      },
    );
  };

  const handleDeleteComment = () => {
    if (!currentComment || !reviewId) {
      logError(
        new GenericError('PendingReviewBanner: Cannot delete comment with missing information', {
          reviewId,
          currentCommentId: currentComment?.id,
        }),
      );
      return;
    }

    // Optimistically remove comment
    onCommentChange(undefined);

    deleteComment(
      { id: reviewId, commentId: currentComment.id },
      {
        onError: (error) => {
          logError(error);
          addNotification(
            {
              title: t('transactions:show.sections.alert.review.notification.deleteComment.error.title'),
              subtitle: t('transactions:show.sections.alert.review.notification.deleteComment.error.subtitle'),
            },
            NotificationType.error,
          );
          // Revert the comment state if deletion fails
          onCommentChange(currentComment);
        },
      },
    );
  };

  const handleCancelEdit = (originalComment?: ReviewComment) => {
    onEditingChange(false);
    onCommentChange(originalComment);
  };

  return {
    handleApprove,
    handleComment,
    handleDeleteComment,
    handleCancelEdit,
    isApprovingMessage,
    isMessageApproved,
  };
};
